/**
 * @param {{
 *   variant_id: number;
 *   product_id: number;
 *   price: number;
 * }} variant
 */
const getPricingInfoForVariant = async (variant) => {
  return await BOLD.pre.addProductJson({
    id: variant.product_id ?? variant.product.id,
    variants: [{
      id: variant.id,
      price: variant.price,
    }],
  });
}

/**
 * Gets the PRE price for a variant
 * 
 * @param {{
 *   variant_id: number;
 *   product_id: number;
 *   price: number;
 * }} variant
 * 
 * @returns {Promise<number>} returns the price in cents if PRE is aware of it. Otherwise NaN is returned
 */
const getPriceForVariant = async (variant) => {
  const product = await getPricingInfoForVariant(variant);
  return product.variants.find(v => v.id === variant.variant_id)?.price ?? NaN;
}

/**
 * Gets pricing information for the variants provided and returns a new array
 * or new variant objects with updated pricing
 * 
 * @template T
 * @param {Array<T & {
 *   product?: {id: number};
 *   product_id?: number;
 *   id: number;
 * }>} variants 
 * @returns {Array<T>}
 */
const getPricingInfoForVariants = async (variants) => {
  if (variants.length === 0) return variants;
  const productMap = new Map();

  // Gathering variants into products
  for (const variant of variants) {
    const productId = variant.product_id ?? variant.product.id;
    const product = !productMap.has(productId)
      ? {id: productId, variants: []}
      : productMap.get(productId);
    productMap.set(productId, product);
    product.variants.push({
      id: variant.id,
      price: variant.price,
    });
  }

  // Getting pricing information
  const products = Array.from(productMap.values());
  const adjustedProducts = await BOLD.pre.addProductsJson(products);
  const adjustedVariants = adjustedProducts.flatMap(product => product.variants);

  // Gluing pricing information back with provided variant information
  return variants.map(variant => ({
    ...variant,
    price: adjustedVariants.find(v => v.id === variant.id).price,
  }));
}

/**
 * Adjusts pricing for variants nested in the provided products. Will return a new array of new products
 * with new variants
 * 
 * @template T
 * @param {Array<T & {
 *   id: number;
 *   variants: Array<{
 *     id: number;
 *     price: number;
 *   }>;
 * }>} variants 
 * @returns {Array<T>}
 */
const adjustProductsPricing = async (products) => {
  if (products.length === 0) return products;
  
  const adjustedProducts = await BOLD.pre.addProductsJson(products);
  return products.map(product => {
    const adjustedProduct = adjustedProducts.find(adjustedProduct => adjustedProduct.id === product.id);
    const adjustedVariants = product.variants.map(variant => {
      const adjustedVariant = adjustedProduct.variants.find(adjustedVariant => adjustedVariant.id === variant.id);

      return {
        ...variant,
        price: adjustedVariant.price,
      };
    });
    
    return {
      ...product,
      variants: adjustedVariants,
    };
  });
}

/**
 * Listens to update events and returns a function that when called will upsubscribe
 * from updates
 * 
 * @param {() => void} fn
 * 
 * @returns {() => void}
 */
const onUpdate = (fn) => {
  BOLD.pre.ready().then(() => {
    BOLD.pre.events.on('update', fn);
  });

  return () => {
    BOLD.pre.ready().then(() => {
      BOLD.pre.events.off('update', fn);
    });
  }
}

/**
 * Similar to onUpdate but calls the function immediately after subscribing to updates
 * 
 * @param {) => void} fn
 * 
 * @returns {() => void}
 */
const onUpdateI = (fn) => {
  const unsub = onUpdate(fn);
  fn();
  return unsub;
}

/**
 * Sets the the customer's zip code
 * 
 * @param {string} zip 
 */
const setOrderDataZip = (zip) => {
  BOLD.pre.setOrderData({customer: {shipping: {
    postal_code: zip,
    country_code: 'US',
  }}});
}

/**
 * Corrects the pricing of the meals
 * 
 * @param {Array<{
 *   id: number;
 *   variants: Array<{
 *     id: number;
 *     price: number;
 *   }>
 * }>} meals 
 * @returns 
 */
const processMeals = async (meals) => {
  const products = meals.map(p => ({
    id: p.id,
    variants: p.variants.map(v => ({
      id: v.id,
      price: v.price,
    })),
  }));
  const preProducts = await BOLD.pre.addProductsJson(products);
  const preVariants = preProducts.map(p => p.variants).flat();

  // Gluing original data togehter with PRE data
  return meals.map(meal => ({
    ...meal,
    variants: meal.variants.map(variant => ({
      ...variant,
      price: preVariants.find(v => v.id === variant.id).price,
    })),
  }));
}

export {
  setOrderDataZip,
  adjustProductsPricing,
  getPriceForVariant,
  onUpdate,
  processMeals,
  onUpdateI,
  getPricingInfoForVariants,
}